import { useId, useState } from "react";
import type { FormEvent } from "react";
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_EXPERIENCES,
  FEEDBACK_EXPERIENCE_LABELS,
  MAX_FEEDBACK_MESSAGE_CHARS,
  MAX_FEEDBACK_NAME_CHARS,
} from "@context/shared";
import type { FeedbackCategory, FeedbackExperience } from "@context/shared";
import { ApiClientError } from "../../api/client";
import { useStudent } from "../../state/StudentProvider";

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug: "Bug",
  idea: "Idea",
  confusing: "Confusing",
  praise: "Praise",
};

const PREFER_NOT_TO_SAY = "prefer_not_to_say";
type ExperienceValue = FeedbackExperience | typeof PREFER_NOT_TO_SAY;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function FeedbackPanel() {
  const { client } = useStudent();
  const id = useId();

  const [message, setMessage] = useState("");
  const [category, setCategory] = useState<FeedbackCategory | "">("");
  const [experience, setExperience] = useState<ExperienceValue>(PREFER_NOT_TO_SAY);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [messageError, setMessageError] = useState<string | null>(null);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const resetForm = () => {
    setMessage("");
    setCategory("");
    setExperience(PREFER_NOT_TO_SAY);
    setName("");
    setEmail("");
    setMessageError(null);
    setCategoryError(null);
    setEmailError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedMessage = message.trim();
    const trimmedEmail = email.trim();
    const nextMessageError = trimmedMessage
      ? null
      : "Tell us what happened before sending your feedback.";
    const nextCategoryError = category ? null : "Choose a category for your feedback.";
    const nextEmailError =
      trimmedEmail && !EMAIL_PATTERN.test(trimmedEmail)
        ? "Enter a valid email address, or leave this field blank."
        : null;

    setMessageError(nextMessageError);
    setCategoryError(nextCategoryError);
    setEmailError(nextEmailError);

    if (nextMessageError || nextCategoryError || nextEmailError || !category) return;

    setBusy(true);
    setSubmitError(null);
    setSent(false);

    try {
      await client.submitFeedback({
        message: trimmedMessage,
        category,
        experience: experience === PREFER_NOT_TO_SAY ? null : experience,
        name: name.trim() || null,
        email: trimmedEmail || null,
        context: {
          view: "feedback",
          userAgent: navigator.userAgent,
          workspaceId: client.workspaceId,
        },
      });
      resetForm();
      setSent(true);
    } catch (error) {
      setSubmitError(
        error instanceof ApiClientError
          ? error.message
          : "Could not send your feedback. Check your connection and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="view-panel active feedback-panel" aria-labelledby={`${id}-heading`}>
      <div className="feedback-header">
        <h2 id={`${id}-heading`}>Share feedback</h2>
        <p className="feedback-intro">
          Tell us what worked, what was confusing, or what you would change. You can send feedback
          even if the writing space is unavailable.
        </p>
      </div>

      <form className="feedback-form" onSubmit={handleSubmit} noValidate aria-busy={busy}>
        <div className="feedback-field">
          <label htmlFor={`${id}-message`}>Message</label>
          <p className="feedback-help" id={`${id}-message-help`}>
            Tell us what happened, what was confusing, or what you would change.
          </p>
          <textarea
            id={`${id}-message`}
            className="feedback-input feedback-textarea"
            value={message}
            maxLength={MAX_FEEDBACK_MESSAGE_CHARS}
            required
            aria-invalid={messageError ? true : undefined}
            aria-describedby={`${id}-message-help ${id}-message-counter ${id}-message-error`}
            onChange={(event) => {
              setMessage(event.target.value);
              if (messageError) {
                setMessageError(event.target.value.trim() ? null : messageError);
              }
            }}
          />
          <div className="feedback-message-meta">
            <span
              id={`${id}-message-error`}
              className="field-error"
              role={messageError ? "alert" : undefined}
            >
              {messageError}
            </span>
            <span id={`${id}-message-counter`} className="feedback-counter">
              {message.length} / {MAX_FEEDBACK_MESSAGE_CHARS}
            </span>
          </div>
        </div>

        <div className="feedback-field">
          <label htmlFor={`${id}-category`}>Category</label>
          <select
            id={`${id}-category`}
            className="feedback-input"
            value={category}
            required
            aria-invalid={categoryError ? true : undefined}
            aria-describedby={`${id}-category-error`}
            onChange={(event) => {
              const value = event.target.value as FeedbackCategory | "";
              setCategory(value);
              if (value) setCategoryError(null);
            }}
          >
            <option value="">Choose a category</option>
            {FEEDBACK_CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {CATEGORY_LABELS[value]}
              </option>
            ))}
          </select>
          <span
            id={`${id}-category-error`}
            className="field-error"
            role={categoryError ? "alert" : undefined}
          >
            {categoryError}
          </span>
        </div>

        <fieldset className="feedback-field feedback-experience">
          <legend>Overall experience</legend>
          <p className="feedback-help">Optional — choose the answer that fits best.</p>
          <div className="feedback-experience-options">
            {FEEDBACK_EXPERIENCES.map((value) => (
              <label className="feedback-experience-option" key={value}>
                <input
                  type="radio"
                  name={`${id}-experience`}
                  value={value}
                  checked={experience === value}
                  onChange={() => setExperience(value)}
                />
                <span>{FEEDBACK_EXPERIENCE_LABELS[value]}</span>
              </label>
            ))}
            <label className="feedback-experience-option">
              <input
                type="radio"
                name={`${id}-experience`}
                value={PREFER_NOT_TO_SAY}
                checked={experience === PREFER_NOT_TO_SAY}
                onChange={() => setExperience(PREFER_NOT_TO_SAY)}
              />
              <span>Prefer not to say</span>
            </label>
          </div>
        </fieldset>

        <div className="feedback-field">
          <label htmlFor={`${id}-name`}>Name (optional)</label>
          <input
            id={`${id}-name`}
            className="feedback-input"
            type="text"
            value={name}
            maxLength={MAX_FEEDBACK_NAME_CHARS}
            autoComplete="name"
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="feedback-field">
          <label htmlFor={`${id}-email`}>Email (optional, for follow-up)</label>
          <p className="feedback-help" id={`${id}-email-help`}>
            We will only use this address to follow up about your feedback.
          </p>
          <input
            id={`${id}-email`}
            className="feedback-input"
            type="email"
            value={email}
            autoComplete="email"
            aria-invalid={emailError ? true : undefined}
            aria-describedby={`${id}-email-help ${id}-email-error`}
            onChange={(event) => {
              const value = event.target.value;
              setEmail(value);
              if (emailError) {
                const trimmed = value.trim();
                setEmailError(!trimmed || EMAIL_PATTERN.test(trimmed) ? null : emailError);
              }
            }}
          />
          <span
            id={`${id}-email-error`}
            className="field-error"
            role={emailError ? "alert" : undefined}
          >
            {emailError}
          </span>
        </div>

        <div className="feedback-actions">
          <button className="primary-button" type="submit" disabled={busy}>
            {busy ? "Sending…" : "Send feedback"}
          </button>
        </div>

        {submitError ? (
          <p className="feedback-error" role="alert">
            {submitError}
          </p>
        ) : null}

        {sent ? (
          <p className="feedback-confirmation" role="status">
            Thanks — your feedback was sent.
          </p>
        ) : null}
      </form>
    </section>
  );
}
